import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';

function ProjectFinances() {
  const { id: projectId } = useParams();

  const [project, setProject] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ description: '', value: '', date: '', status: 'Em aberto' });
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editingExpense, setEditingExpense] = useState({});
  const [isAdmin, setIsAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        console.log('User data:', userData);
        const admin = userData?.role === 'admin';
        setIsAdmin(admin);
      } else {
        setIsAdmin(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      if (projectSnap.exists()) {
        const proj = projectSnap.data();
        setProject(proj);
      }

      const expensesRef = collection(db, 'projects', projectId, 'expenses');
      const expensesSnap = await getDocs(expensesRef);
      const expensesList = expensesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        value: Number(doc.data().value) || 0,
        status: doc.data().status || 'Em aberto'
      }));
      setExpenses(expensesList);
    };

    fetchData();
  }, [projectId]);

  const handleAddExpense = async () => {
    if (!newExpense.description || !newExpense.value || !newExpense.date) return;

    const expensesRef = collection(db, 'projects', projectId, 'expenses');
    await addDoc(expensesRef, {
      ...newExpense,
      value: Number(newExpense.value),
      createdAt: new Date().toISOString()
    });
    setNewExpense({ description: '', value: '', date: '', status: 'Em aberto' });
    reloadExpenses();
  };

  const handleDeleteExpense = async (expenseId) => {
    const expenseRef = doc(db, 'projects', projectId, 'expenses', expenseId);
    await deleteDoc(expenseRef);
    reloadExpenses();
  };

  const handleEditExpense = (expense) => {
    setEditingExpenseId(expense.id);
    setEditingExpense(expense);
  };

  const handleSaveEdit = async () => {
    const expenseRef = doc(db, 'projects', projectId, 'expenses', editingExpenseId);
    await updateDoc(expenseRef, {
      ...editingExpense,
      value: Number(editingExpense.value),
    });
    setEditingExpenseId(null);
    setEditingExpense({});
    reloadExpenses();
  };

  const reloadExpenses = async () => {
    const expensesRef = collection(db, 'projects', projectId, 'expenses');
    const expensesSnap = await getDocs(expensesRef);
    const expensesList = expensesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      value: Number(doc.data().value) || 0,
      status: doc.data().status || 'Em aberto'
    }));
    setExpenses(expensesList);
  };

  const total = expenses.reduce((sum, exp) => sum + (exp.value || 0), 0);
  const saldoEmAberto = expenses
    .filter(exp => exp.status === 'Em aberto')
    .reduce((sum, exp) => sum + (exp.value || 0), 0);

  const getStatusColor = (status, date) => {
    const today = new Date().toISOString().split('T')[0];
    if (status === 'Pago') return 'text-green-600';
    if (status === 'Em aberto' && date < today) return 'text-red-600';
    if (status === 'Em aberto') return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (isLoading || isAdmin === null) {
    return <div className="p-6">Carregando...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Finanças do Projeto: {project?.name}</h1>

      <div className="mb-4 space-y-1">
        <p><strong>Total:</strong> {formatCurrency(total)}</p>
        <p><strong>Saldo em Aberto:</strong> {formatCurrency(saldoEmAberto)}</p>
      </div>

      {isAdmin && (
        <div className="mb-6 space-y-2">
          <h2 className="text-xl font-semibold">Nova Parcela</h2>
          <input
            type="text"
            placeholder="Descrição"
            value={newExpense.description}
            onChange={e => setNewExpense({ ...newExpense, description: e.target.value })}
            className="border p-2 mr-2"
          />
          <input
            type="number"
            placeholder="Valor"
            value={newExpense.value}
            onChange={e => setNewExpense({ ...newExpense, value: e.target.value })}
            className="border p-2 mr-2"
          />
          <input
            type="date"
            value={newExpense.date}
            onChange={e => setNewExpense({ ...newExpense, date: e.target.value })}
            className="border p-2 mr-2"
          />
          <select
            value={newExpense.status}
            onChange={e => setNewExpense({ ...newExpense, status: e.target.value })}
            className="border p-2 mr-2"
          >
            <option>Em aberto</option>
            <option>Pago</option>
          </select>
          <button
            onClick={handleAddExpense}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Adicionar
          </button>
        </div>
      )}

      <h2 className="text-xl font-semibold mb-2">Parcelas</h2>

      <div className="grid grid-cols-5 font-semibold border-b py-2">
        <div>Parcela</div>
        <div>Valor</div>
        <div>Data de Vencimento</div>
        <div>Situação</div>
        <div>Ações</div>
      </div>

      <ul className="space-y-2">
        {expenses.length === 0 ? (
          <li>Nenhuma parcela registrada.</li>
        ) : (
          expenses.map(exp => (
            <li key={exp.id} className="grid grid-cols-5 items-center border-b py-2">
              {editingExpenseId === exp.id ? (
                isAdmin ? (
                  <>
                    <input
                      type="text"
                      value={editingExpense.description}
                      onChange={e => setEditingExpense({ ...editingExpense, description: e.target.value })}
                      className="border p-1"
                    />
                    <input
                      type="number"
                      value={editingExpense.value}
                      onChange={e => setEditingExpense({ ...editingExpense, value: e.target.value })}
                      className="border p-1"
                    />
                    <input
                      type="date"
                      value={editingExpense.date}
                      onChange={e => setEditingExpense({ ...editingExpense, date: e.target.value })}
                      className="border p-1"
                    />
                    <select
                      value={editingExpense.status}
                      onChange={e => setEditingExpense({ ...editingExpense, status: e.target.value })}
                      className="border p-1"
                    >
                      <option>Em aberto</option>
                      <option>Pago</option>
                    </select>
                    <button
                      onClick={handleSaveEdit}
                      className="bg-green-500 text-white px-2 py-1 rounded"
                    >
                      Salvar
                    </button>
                  </>
                ) : (
                  <div className="col-span-5 text-gray-500">Apenas admins podem editar</div>
                )
              ) : (
                <>
                  <div>{exp.description}</div>
                  <div>{formatCurrency(exp.value)}</div>
                  <div>{exp.date}</div>
                  <div className={getStatusColor(exp.status, exp.date)}>{exp.status}</div>
                  <div className="space-x-2">
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleEditExpense(exp)}
                          className="text-blue-500 hover:underline"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(exp.id)}
                          className="text-red-500 hover:underline"
                        >
                          Excluir
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

export default ProjectFinances;
